import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslations } from "use-intl";

import { Badge } from "@lawline/ui/components/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";
import { cn } from "@lawline/ui/core/cn";

import { formatDate } from "@platform/fmt";

import type { AdminTenant, BillingStatus } from "../tenants.types";
import { TenantPlanActions } from "./tenant-plan-actions";

const BILLING_CLASS: Record<BillingStatus, string> = {
	active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
	pending: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
	past_due: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
	canceled: "bg-muted text-muted-foreground",
};

export function TenantsTable({ tenants }: { tenants: AdminTenant[] }) {
	const t = useTranslations("admin.tenants.table");
	const tRoot = useTranslations("admin.tenants");

	const columns = useMemo<ColumnDef<AdminTenant>[]>(
		() => [
			{
				accessorKey: "name",
				header: t("columns.name"),
				cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
			},
			{
				accessorKey: "slug",
				header: t("columns.slug"),
				cell: ({ row }) => (
					<span className="text-muted-foreground">{row.original.slug ?? "-"}</span>
				),
			},
			{
				id: "plan",
				header: t("columns.plan"),
				cell: ({ row }) => t(`plans.${row.original.rateTier}`),
			},
			{
				id: "billing",
				header: t("columns.billing"),
				cell: ({ row }) => (
					<Badge className={cn("border-transparent", BILLING_CLASS[row.original.billingStatus])}>
						{t(`billingStatus.${row.original.billingStatus}`)}
					</Badge>
				),
			},
			{
				accessorKey: "createdAt",
				header: t("columns.created"),
				cell: ({ row }) => formatDate(row.original.createdAt),
			},
			{
				id: "actions",
				header: "",
				cell: ({ row }) => (
					<div className="text-right">
						<TenantPlanActions tenant={row.original} />
					</div>
				),
			},
		],
		[t],
	);

	const table = useReactTable({
		data: tenants,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className="overflow-hidden rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((group) => (
						<TableRow key={group.id}>
							{group.headers.map((header) => (
								<TableHead key={header.id}>
									{flexRender(header.column.columnDef.header, header.getContext())}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={columns.length}
								className="text-muted-foreground h-20 text-center text-sm"
							>
								{tRoot("emptyState")}
							</TableCell>
						</TableRow>
					) : (
						table.getRowModel().rows.map((row) => (
							<TableRow key={row.id}>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
	);
}
